/**
 * Angular FormInput Component
 *
 * Migrated from the AngularJS `formInput` attribute directive
 * (apps/b2b-ui/common/directives/formInput/).
 *
 * It is a presentational wrapper that renders a form-group container with an
 * optional label, an error message, and a slot (`<ng-content>`) for the inner
 * input element(s). It is NOT a form control — consumers wire `ngModel` /
 * `formControl` to the input projected inside.
 *
 * Usage in Angular templates:
 *   <app-form-input name="email" label="Email"
 *                   [validTest]="form.email.valid"
 *                   errorMessage="Please enter a valid email">
 *     <input id="email" type="email" [(ngModel)]="model.email" required>
 *   </app-form-input>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <ng-form-input name="email" label="Email"
 *                  valid-test="$ctrl.form.email.$valid"
 *                  error-message="'Please enter a valid email'">
 *     <input id="email" type="email" data-ng-model="$ctrl.model.email">
 *   </ng-form-input>
 *
 * Notes on parity with the original directive:
 * - The original isolate scope exposed `placeholderValue` (`@placeholdervalue`)
 *   but the template never read it; the binding was dead. It is dropped here.
 * - The original template had `data-ng-show="!hasAddon"` referencing a name
 *   that was not in the isolate scope, so the inner block was always visible.
 *   We mirror that behavior by always rendering it.
 */
import { booleanAttribute, Component, Input, computed, signal } from '@angular/core';
import { NgIf } from '@angular/common';

/**
 * The host element acts as the wrapper (replaces the outer `<div class="form-input …">`
 * the original AJS directive emitted with `replace: true`), so layout classes
 * passed in by consumers — e.g. `class="col-sm-6"` — stay on the same element as
 * `form-input` / `form-group`, preserving the legacy CSS contract.
 *
 * All host classes are exposed as `[class.x]` bindings (not a static `'class'`
 * entry) so they are applied via `classList.add/remove` and DO NOT clobber any
 * `class="…"` provided by the consumer — critical under `downgradeComponent`,
 * where AJS sets the element's class attribute before Angular initializes the
 * host bindings.
 */
@Component({
  selector: 'app-form-input',
  standalone: true,
  imports: [NgIf],
  template: require('./form-input.component.html'),
  styles: [require('./form-input.component.less')],
  host: {
    '[class.form-input]': 'true',
    '[class.form-group]': '!isPanelGroup',
    '[class.panel-form-group]': 'isPanelGroup',
    '[class.form-input-error]': 'isInvalid()',
    '[class.generic-input-error]': 'isInvalid()',
  },
})
export class FormInputComponent {
  /** Id of the wrapped input — used for the `<label for>` association and the error-note id. */
  @Input() name = '';

  /** Visible label text. */
  @Input() label = '';

  /** When truthy, the label is rendered with the `sr-only` class. */
  @Input({ transform: booleanAttribute }) hideLabel = false;

  private readonly _validTest = signal(true);
  /** Boolean expression — when `false`, the wrapper renders in its error state. */
  @Input({ transform: booleanAttribute })
  get validTest(): boolean { return this._validTest(); }
  set validTest(value: boolean) { this._validTest.set(value); }

  /** Error message shown next to the input when `validTest` is `false`. */
  @Input() errorMessage = '';

  private readonly _isPanelGroup = signal(false);
  /** When truthy, uses the `panel-form-group` group class instead of `form-group`. */
  @Input({ transform: booleanAttribute })
  get isPanelGroup(): boolean { return this._isPanelGroup(); }
  set isPanelGroup(value: boolean) { this._isPanelGroup.set(value); }

  /** When truthy, the `<label>` element is omitted entirely. */
  @Input({ transform: booleanAttribute }) removeLabel = false;

  /** Extra screen-reader-only text appended inside the label. */
  @Input() srOnly = '';

  /** `form-group` (default) or `panel-form-group` — matches the AJS directive. */
  readonly groupTypeClass = computed(() => this._isPanelGroup() ? 'panel-form-group' : 'form-group');

  /** True when the wrapper should render in its error state. */
  readonly isInvalid = computed(() => !this._validTest());
}
